/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { v4 as uuidv4 } from "uuid";
import { observable, action } from "mobx";
import { logging as loggingApi } from "dc-core";
import FloatingWindow from "./FloatingWindow";
import { EVENTS } from "./IFloatingWindowConstants";

let logger = loggingApi.getLogger({ context: "floatingWindow", dropinId: "dc-preview-dropin" });

class FloatingWindowsManager {
  @observable floatingWindowMap = new Map();

  @action
  addWindow({ id = uuidv4(), targetRect, targetPoint, isDraggable, component = null, margin = 10 }) {
    if (!targetPoint && !targetRect) {
      throw new Error("Either targetRect or targetPoint must be provided");
    }
    let floatingWindowInstance = null;

    if (targetPoint) {
      floatingWindowInstance = new FloatingWindow({
        id,
        isDraggable,
        component,
        margin,
        targetPoint,
        destroy: () => this.destroyWindow(id),
        onDestroy: listener => this.onDestroyWindow(id, listener)
      });
    } else if (targetRect) {
      floatingWindowInstance = new FloatingWindow({
        id,
        isDraggable,
        component,
        margin,
        targetRect,
        destroy: () => this.destroyWindow(id),
        onDestroy: listener => this.onDestroyWindow(id, listener)
      });
    }

    if (!floatingWindowInstance) {
      throw new Error("FloatingWindow instance could not be created");
    }
    this.floatingWindowMap.set(id, floatingWindowInstance);
    return floatingWindowInstance;
  }

  @action
  cleanup() {
    // First emit DESTROY event for each window to clean up listeners
    Array.from(this.floatingWindowMap.values()).forEach(window => {
      window.emit(EVENTS.DESTROY);
      logger.info(`Window with id ${window.windowId} destroyed during cleanup`);
    });
    // Then clear the map
    this.floatingWindowMap.clear();
  }

  getWindowInstance(id) {
    return this.floatingWindowMap.get(id);
  }

  getAllWindowInstances() {
    return Array.from(this.floatingWindowMap.values());
  }

  @action
  destroyWindow(id) {
    const floatingWindowInstance = this.floatingWindowMap.get(id);
    if (floatingWindowInstance) {
      this.floatingWindowMap.delete(id);
      floatingWindowInstance.emit(EVENTS.DESTROY);
      logger.info(`Window with id ${id} destroyed`);
    } else {
      logger.error(`Window with id ${id} does not exist`);
      throw new Error(`Window with id ${id} does not exist`);
    }
  }

  onDestroyWindow(id, listener) {
    const floatingWindowInstance = this.floatingWindowMap.get(id);
    if (floatingWindowInstance) {
      floatingWindowInstance.on(EVENTS.DESTROY, listener);
    }
  }
}

const floatingWindowsManager = new FloatingWindowsManager();
export default floatingWindowsManager;
